/**
 * Pose one hinge at a sample angle, with its correctives driven — ADR-028's
 * shared posing recipe.
 *
 * The crease derivation and the crease probe both need "put this elbow at 120°
 * exactly the way the app would". So does the E1 probe, which today
 * REPLICATES the live door's arithmetic rather than calling it — a drift risk
 * the survey found and this module exists to stop spreading. One recipe, one
 * place, and the correctives are driven through the kernel evaluator, which is
 * the same code the viewport frame driver runs.
 *
 * Driving the correctives is not a nicety: E3 corrects what is LEFT after E1's
 * half-rotation and E2's leaves have had their say. Derive against a bend where
 * the helper sat inert and the morph re-fixes what the helper already fixed —
 * an over-inflated elbow, and very hard to attribute after the fact.
 *
 * The axis comes from the PROFILE (measured, in the driver's bind-local frame)
 * rather than from limb geometry, because that is the axis the live door reads
 * θ about. A derivation posed about a different axis would be a correction for
 * a bend the character never performs.
 *
 * Depends on: three, kernel. Depended on by: the crease derivation and probes.
 */
import { Object3D, Quaternion, Vector3 } from 'three';
import {
  evaluateHingeCorrectives,
  hingeBendAngle,
  type ResolvedHinge,
  type RigDescriptor,
} from '@harryhausen/kernel';

export class HingePoseError extends Error {}

/** Undo what `poseHingeAt` did, restoring every node it touched. */
export type RestorePose = () => void;

/**
 * Pose `hinge` at `thetaDeg` within `scene`, driving its helper and leaves the
 * way the live door does, and return the function that puts everything back.
 *
 * The scene is left with world matrices updated, ready to skin from.
 */
export function poseHingeAt(
  scene: Object3D,
  rig: RigDescriptor,
  hinge: ResolvedHinge,
  thetaDeg: number,
): RestorePose {
  const driverNode = scene.getObjectByName(hinge.driver);
  const helperNode = scene.getObjectByName(hinge.helper);
  if (driverNode === undefined || helperNode === undefined) {
    throw new HingePoseError(
      `hinge "${hinge.helper}": driver or helper missing from the scene — the graft and the scene disagree`,
    );
  }
  const driverBone = rig.bones.find((b) => b.name === hinge.driver);
  const helperBone = rig.bones.find((b) => b.name === hinge.helper);
  if (driverBone === undefined || helperBone === undefined) {
    throw new HingePoseError(`hinge "${hinge.helper}": driver or helper missing from the rig descriptor`);
  }

  // The axis, carried from the driver's BIND-local frame into world by the
  // driver's own bind rotation — the rig descriptor's, not the scene's, so a
  // scene that happens to be mid-clip cannot move the measurement.
  const wb = driverBone.worldBind.rotation;
  const axis = new Vector3(
    hinge.profile.hingeAxisLocal.x,
    hinge.profile.hingeAxisLocal.y,
    hinge.profile.hingeAxisLocal.z,
  ).applyQuaternion(new Quaternion(wb.x, wb.y, wb.z, wb.w));
  if (axis.lengthSq() < 1e-12) throw new HingePoseError(`hinge "${hinge.helper}": its declared axis is degenerate`);
  axis.normalize();

  const leafNodes = hinge.leaves.map((leaf) => {
    const node = scene.getObjectByName(leaf.name);
    if (node === undefined) throw new HingePoseError(`leaf "${leaf.name}": missing from the scene`);
    return { leaf, node, saved: node.position.clone() };
  });
  const savedDriver = driverNode.quaternion.clone();
  const savedHelper = helperNode.quaternion.clone();

  const bindLocal = new Quaternion(
    driverBone.localBind.rotation.x,
    driverBone.localBind.rotation.y,
    driverBone.localBind.rotation.z,
    driverBone.localBind.rotation.w,
  );
  const rotation = new Quaternion().setFromAxisAngle(axis, (thetaDeg * Math.PI) / 180);
  const parent = driverNode.parent;
  if (parent !== null) parent.updateWorldMatrix(true, false);
  const parentWorld = parent === null ? new Quaternion() : parent.getWorldQuaternion(new Quaternion());
  // Apply the world-space bend about the driver's own origin, then land back
  // in the parent's frame where a local quaternion lives.
  driverNode.quaternion.copy(parentWorld.clone().invert().multiply(rotation).multiply(parentWorld).multiply(bindLocal));

  // Drive the correctives EXACTLY as the frame driver does: the kernel
  // evaluator, reading the driver's current local rotation against its bind.
  const q = driverNode.quaternion;
  const reading = hingeBendAngle(hinge.profile, driverBone.localBind.rotation, { x: q.x, y: q.y, z: q.z, w: q.w });
  const out = evaluateHingeCorrectives(hinge.profile, reading.thetaDeg);
  for (const pose of out.helpers) {
    if (pose.rotation !== undefined && pose.name === hinge.helper) {
      const hb = helperBone.localBind.rotation;
      const local = new Quaternion(hb.x, hb.y, hb.z, hb.w).multiply(
        new Quaternion(pose.rotation.x, pose.rotation.y, pose.rotation.z, pose.rotation.w),
      );
      helperNode.quaternion.copy(local);
    }
    if (pose.translation !== undefined) {
      const entry = leafNodes.find((l) => l.leaf.name === pose.name);
      if (entry === undefined) continue;
      entry.node.position.set(
        entry.leaf.bindLocalPosition.x + pose.translation.x * entry.leaf.scaleWorld,
        entry.leaf.bindLocalPosition.y + pose.translation.y * entry.leaf.scaleWorld,
        entry.leaf.bindLocalPosition.z + pose.translation.z * entry.leaf.scaleWorld,
      );
    }
  }
  scene.updateMatrixWorld(true);

  return () => {
    driverNode.quaternion.copy(savedDriver);
    helperNode.quaternion.copy(savedHelper);
    for (const { node, saved } of leafNodes) node.position.copy(saved);
    scene.updateMatrixWorld(true);
  };
}

/**
 * The angle a declared crease morph is derived AT: the ramp station where it
 * reaches its maximum.
 *
 * Deriving each stage where it stands ALONE is what keeps the stages
 * independent. Daz ships two correctives per elbow and lets both be active at
 * once, which makes deriving the second a linear solve against the first. We
 * author the ramps, so we simply decline that: at each sample angle exactly one
 * morph carries weight, and each is derived against raw LBS with no coupling.
 * This function refuses a declaration that breaks the arrangement, because the
 * failure it prevents is silent — two stages derived on top of each other look
 * like one over-strong crease.
 */
export function derivationAngle(hinge: ResolvedHinge, morphName: string): number {
  const morph = hinge.profile.morphs.find((m) => m.name === morphName);
  if (morph === undefined) throw new HingePoseError(`hinge "${hinge.helper}" declares no morph "${morphName}"`);
  let best = morph.ramp.points[0]!;
  for (const point of morph.ramp.points) if (point.value > best.value) best = point;
  if (!(best.value > 0)) {
    throw new HingePoseError(`morph "${morphName}": its ramp never rises above zero, so it names no pose to derive at`);
  }
  // THE PEAK MUST BE 1, and this is not pedantry (Sol, review of #113).
  //
  // The derivation makes a FULL-STRENGTH correction at this angle — the whole
  // difference between what the skin does and what it should do. At play time
  // the runtime multiplies that by the ramp's value. So a ramp peaking at 0.5
  // applies half the correction it was derived for, and one peaking above 1
  // overshoots it — silently, on every character, forever. It would read as
  // "the crease is weak on some rigs", which is not a sentence anyone could
  // trace back to a declaration.
  if (Math.abs(best.value - 1) > 1e-6) {
    throw new HingePoseError(
      `morph "${morphName}" peaks at ${best.value} rather than 1 — the shape is derived at full strength for ` +
        `${best.thetaDeg}° and then multiplied by the ramp, so any other peak silently under- or over-applies it. ` +
        `Shape the ramp's WIDTH, never its height.`,
    );
  }
  for (const other of hinge.profile.morphs) {
    if (other.name === morphName) continue;
    const weight = evaluateHingeCorrectives(hinge.profile, best.thetaDeg).morphWeights.find((w) => w.name === other.name);
    if (weight !== undefined && Math.abs(weight.weight) > 1e-6) {
      throw new HingePoseError(
        `morph "${morphName}" peaks at ${best.thetaDeg}° where "${other.name}" also carries ${weight.weight.toFixed(3)} — ` +
          `each stage must stand ALONE at its own sample angle, or the two are derived on top of each other`,
      );
    }
  }
  return best.thetaDeg;
}
